import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Newspaper, Gamepad2, ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

const TABS = [
  ["", "All"],
  ["new-citizen", "New Citizen"],
  ["company", "Company"],
  ["asset-packs", "Asset Packs"],
];

const CATEGORY_COLORS = {
  "new-citizen": "#2E6BFF",
  "company": "#FF6B1A",
  "asset-packs": "#00E676",
};

const MD_COMPONENTS = {
  p: (props) => <p className="mb-3 last:mb-0" {...props} />,
  strong: (props) => <strong className="text-zinc-100 font-semibold" {...props} />,
  a: (props) => <a className="text-nc-blue hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
  ul: (props) => <ul className="mb-3 flex flex-col gap-1.5" {...props} />,
  li: (props) => <li className="pl-4 relative before:content-['//'] before:absolute before:left-0 before:text-nc-orange" {...props} />,
};

export default function NewsPage() {
  const [posts, setPosts] = useState(null);
  const [steamNews, setSteamNews] = useState(null);
  const [tab, setTab] = useState("");

  useEffect(() => {
    api.get("/devlog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
    api.get("/steam/news").then((r) => setSteamNews(r.data.items || [])).catch(() => setSteamNews([]));
  }, []);

  const filtered = (posts || []).filter((p) => !tab || p.category === tab);

  return (
    <div data-testid="news-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title="News & Devlogs" description="Development updates from Specpixel Entertainment: New Citizen progress, studio announcements, releases and behind-the-scenes devlogs." path="/news" keywords="New Citizen devlog, Specpixel news, game development updates" />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-4">Specpixel Entertainment</p>
          <h1 data-testid="news-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50 flex items-center gap-4">
            <Newspaper className="w-8 h-8 text-nc-blue" /> News Hub
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Every transmission in one place — New Citizen development updates, company news and asset pack releases.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 w-full">
        {/* Steam announcements — live from the Steam news feed */}
        {steamNews === null ? null : steamNews.length > 0 && (
          <section data-testid="steam-news" className="mb-12">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <p className="font-mono text-[10px] tracking-[0.35em] text-[#66c0f4] uppercase flex items-center gap-2"><Gamepad2 className="w-4 h-4" /> Steam Announcements</p>
              <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em]">auto-synced from store.steampowered.com</span>
            </div>
            <div className="flex flex-col gap-3">
              {steamNews.map((n, i) => (
                <motion.a
                  key={n.id || i}
                  data-testid={`steam-news-${i}`}
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-[#1b2838] bg-[#101822]/50 p-5 hover:border-[#66c0f4]/50 transition-colors duration-300 block"
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                >
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <h3 className="font-sans font-bold text-sm text-zinc-100 group-hover:text-[#66c0f4] transition-colors">{n.title}</h3>
                    <span className="font-mono text-[9px] text-zinc-500 shrink-0">{String(n.date).slice(0, 10)}</span>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500 leading-relaxed mt-2 line-clamp-2">{n.excerpt}</p>
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#66c0f4] mt-3 inline-flex items-center gap-1.5">Read on Steam <ExternalLink className="w-3 h-3" /></span>
                </motion.a>
              ))}
            </div>
          </section>
        )}

        <p className="font-mono text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-5">Studio Transmissions</p>
        <div data-testid="news-tabs" className="flex flex-wrap gap-2 mb-10">
          {TABS.map(([value, label]) => (
            <button
              key={value}
              data-testid={`news-tab-${value || "all"}`}
              onClick={() => setTab(value)}
              className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${
                tab === value ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {!posts ? (
          <p data-testid="news-loading" className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">Loading transmissions...</p>
        ) : filtered.length === 0 ? (
          <p data-testid="news-empty" className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase">No transmissions in this channel yet.</p>
        ) : (
          <div className="flex flex-col gap-5">
            {filtered.map((post, i) => {
              const color = CATEGORY_COLORS[post.category] || "#2E6BFF";
              return (
                <motion.article
                  key={post.id}
                  data-testid={`news-post-${i + 1}`}
                  className="border-l-2 bg-black/40 border border-white/10 p-6 hover:border-white/25 transition-colors duration-300"
                  style={{ borderLeftColor: color }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 border" style={{ color, borderColor: `${color}55`, backgroundColor: `${color}11` }}>
                        {post.category}
                      </span>
                      <h2 data-testid={`news-post-${i + 1}-title`} className="font-sans font-bold uppercase text-xl text-zinc-100">{post.title}</h2>
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">{String(post.createdAt).slice(0, 10)}</span>
                  </div>
                  {post.image && <img src={post.image} alt={post.title} loading="lazy" className="w-full max-h-96 object-cover border border-white/10 mt-4" />}
                  <div className="font-mono text-xs text-zinc-400 leading-relaxed mt-4">
                    <ReactMarkdown components={MD_COMPONENTS}>{post.body}</ReactMarkdown>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
